import type { BudgetCategoryAllocations, BudgetCategoryKey } from '@/types/budget';

export const BUDGET_CATEGORY_KEYS: BudgetCategoryKey[] = [
  'flight',
  'lodging',
  'food',
  'transport',
  'activity',
  'shopping',
  'other',
];

export const DEFAULT_BUDGET_CATEGORY_ALLOCATIONS: Record<BudgetCategoryKey, number> = {
  flight: 0.35,
  lodging: 0.3,
  food: 0.15,
  transport: 0.08,
  activity: 0.07,
  shopping: 0.03,
  other: 0.02,
};

const clamp01 = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
};

/**
 * Normalize a user-provided allocation map into fractions that sum to ~1.
 * Accepts either:
 * - fractions (e.g. 0.35) or
 * - percents (e.g. 35 or 35.0).
 *
 * Fills missing categories from defaults and renormalizes to sum=1.
 */
export function normalizeBudgetCategoryAllocations(
  input?: BudgetCategoryAllocations | null,
): Record<BudgetCategoryKey, number> {
  const base: Record<BudgetCategoryKey, number> = { ...DEFAULT_BUDGET_CATEGORY_ALLOCATIONS };
  if (!input) return base;

  const raw: Record<BudgetCategoryKey, number> = { ...base };

  // Copy input values (ignore unknown keys)
  for (const key of BUDGET_CATEGORY_KEYS) {
    const v = input[key];
    if (typeof v === 'number' && Number.isFinite(v)) {
      raw[key] = v;
    }
  }

  // Detect percent-style values (any value > 1.5 is almost certainly a percent)
  const anyLooksPercent = BUDGET_CATEGORY_KEYS.some((k) => raw[k] > 1.5);
  const normalized = { ...raw };
  if (anyLooksPercent) {
    for (const key of BUDGET_CATEGORY_KEYS) {
      normalized[key] = normalized[key] / 100;
    }
  }

  // Clamp and renormalize
  let sum = 0;
  for (const key of BUDGET_CATEGORY_KEYS) {
    normalized[key] = clamp01(normalized[key]);
    sum += normalized[key];
  }

  if (sum <= 0) {
    return { ...DEFAULT_BUDGET_CATEGORY_ALLOCATIONS };
  }

  const out: Record<BudgetCategoryKey, number> = { ...normalized } as Record<BudgetCategoryKey, number>;
  for (const key of BUDGET_CATEGORY_KEYS) {
    out[key] = out[key] / sum;
  }

  return out;
}

